import { graphql, useLazyLoadQuery } from 'react-relay';
import { Button, Card, CardBody, CardFooter, CardHeader, Container, useDisclosure } from '@vitvio/design-system';
import type { organizationManagementQuery } from './__generated__/organizationManagementQuery.graphql';
import { CreateHospitalModal } from './CreateHospitalModal';
import { useState } from 'react';
import { OperatingRoomManagement } from './OperatingRoomManagement';

const query = graphql`
  query organizationManagementQuery {
    userOrganization {
      id
      name
      hospitals(first: 10) {
        edges {
          node {
            id
            name
            timezone

            operatingRooms(first: 20) {
              ...OperatingRoomManagementFragment
            }
          }
        }
      }
    }
  }
`;

export function OrganizationManagement() {
  const [fetchKey, setFetchKey] = useState(0);
  const data = useLazyLoadQuery<organizationManagementQuery>(
    query,
    {},
    {
      fetchKey,
      fetchPolicy: 'store-and-network',
    },
  );

  const { isOpen: isHospitalModalOpen, onClose: onCloseHospitalModal, onOpen: onOpenHospitalModal } = useDisclosure();

  function triggerReload() {
    setFetchKey(fetchKey + 1);
  }

  function handleHospitalSubmit(shouldReload: boolean) {
    if (shouldReload) {
      triggerReload();
    }
    onCloseHospitalModal();
  }

  if (!data) {
    return <div>Loading</div>;
  }

  if (data?.userOrganization?.hospitals?.edges?.length === 0) {
    return (
      <Container paddingTop="30px">
        <CreateHospitalModal isOpen={isHospitalModalOpen} onClose={handleHospitalSubmit} />
        <Card>
          <CardBody>No hospitals available. Do you want to create one?</CardBody>
          <CardFooter textAlign={'center'}>
            <Button onClick={onOpenHospitalModal}>Create Hospital</Button>
          </CardFooter>
        </Card>
      </Container>
    );
  }

  return (
    <Container paddingTop="30px">
      <CreateHospitalModal isOpen={isHospitalModalOpen} onClose={handleHospitalSubmit} />
      {data.userOrganization?.hospitals?.edges?.map((hospital) => (
        <Card marginBottom="20px" key={hospital?.node?.id}>
          <CardHeader>
            {hospital?.node?.name} - {hospital?.node?.timezone}
          </CardHeader>
          <CardBody>
            <OperatingRoomManagement
              operatingRoom={hospital?.node?.operatingRooms || undefined}
              hospitalId={hospital?.node?.id || ''}
              reloadFn={triggerReload}
            />
          </CardBody>
        </Card>
      ))}
      <Card>
        <CardBody>
          Add additional hospitals <Button onClick={onOpenHospitalModal}>Create Hospital</Button>
        </CardBody>
      </Card>
    </Container>
  );
}

export default OrganizationManagement;
